/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { RootState } from "app/store"
import { InfoPopover, setupPopovers } from "components/InfoPopover"
import DataTable from "features/results/DataTable"
import _ from "lodash"
import { useEffect, useLayoutEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setResultsFresh } from "./ResultsSlice"

const Results = () => {
  const { results, fresh: areResultsFresh } = useSelector((state: RootState) => state.results)
  const dispatch = useDispatch()

  useEffect(() => {
    setupPopovers()
  }, [results])

  const consumptionAmountBasedTables = results?.maybeUserConsumptionAmountResults
    ? [
        {
          id: "user-cape-failure-rates",
          caption: null,
          data: results.maybeUserConsumptionAmountResults.capeFailureRates,
        },
        {
          id: "user-drawdown-failure-rates",
          caption: null,
          data: results.maybeUserConsumptionAmountResults.drawdownFailureRates,
        },
      ]
    : []

  const supplementaryTables = results
    ? [
        {
          id: "cape-consumption-amounts",
          caption: "Initial safe consumption amounts (p.a.) to target different failure rates",
          data: results.capeConsumptionAmounts,
        },
        {
          id: "drawdown-consumption-amounts",
          caption: "Initial safe consumption amounts (p.a.) conditional on S&P500 drawdown",
          data: results.drawdownConsumptionAmounts,
        },
        {
          id: "cape-consumption-rates",
          caption: "Initial safe consumption rates (p.a.) to target different failure rates",
          data: results.capeConsumptionRates,
        },
        {
          id: "drawdown-consumption-rates",
          caption:
            "Initial safe consumption rates (p.a.) to target different failure rates, conditional on S&P500 drawdown",
          data: results.drawdownConsumptionRates,
        },
        {
          id: "cape-failure-rates",
          caption: "Failure rates of different initial consumption rates (p.a.)",
          data: results.capeFailureRates,
        },
        {
          id: "drawdown-failure-rates",
          caption:
            "Failure rates conditional on S&P500 drawdown of different initial consumption rates (p.a.)",
          data: results.drawdownFailureRates,
        },
      ]
    : []

  const totalTableCount = _.size(consumptionAmountBasedTables) + _.size(supplementaryTables)

  const versionRef = useRef(0)
  const renderedTablesRef = useRef(0)

  useLayoutEffect(() => {
    // Increment version and reset counter.
    // Runs before the browser has a chance to paint
    // and before the DOM is updated,
    // i.e. before the tables are rendered
    versionRef.current += 1
    renderedTablesRef.current = 0
  }, [results])

  const handleTableRendered = (callbackVersion: number) => {
    // Only count if the callback's version matches the current version.
    // Prevents counting tables from previous result-set renders.
    if (callbackVersion !== versionRef.current) return
    renderedTablesRef.current += 1
    if (renderedTablesRef.current === totalTableCount) {
      requestAnimationFrame(() => {
        dispatch(setResultsFresh(true))
      })
    }
  }

  if (!results) {
    return null
  }

  const supplementaryTablesHeader =
    consumptionAmountBasedTables.length > 0 ? "Supplementary Results" : "Results"

  return (
    <>
      {!areResultsFresh && (
        <div id="stale-message" className="alert alert-info" role="alert">
          Please resubmit to update.
        </div>
      )}
      {consumptionAmountBasedTables.length > 0 && (
        <section
          className={`my-3 mb-5${areResultsFresh ? "" : " opacity-25"}`}
          aria-describedby={!areResultsFresh ? "stale-message" : undefined}
          aria-live="polite"
        >
          <h2 className="pb-3">
            Failure Rates
            <InfoPopover
              htmlContent={true}
              content={`
                <p>
                  These rates are the percentages of historical retirements with your 
                  specified parameters that would have run out of money.
                </p>
                <p>
                  Each column gives a rate for retirements that began within particular periods 
                  or that share initial market conditions.
                </p>`}
            />
          </h2>
          {consumptionAmountBasedTables.map(table => (
            <DataTable
              key={table.id}
              caption={table.caption}
              tableData={table.data}
              onRendered={() => {
                handleTableRendered(versionRef.current)
              }}
            />
          ))}
        </section>
      )}
      <section
        className={`my-3 mb-5${areResultsFresh ? "" : " opacity-50"}`}
        id="results"
        aria-describedby={!areResultsFresh ? "stale-message" : undefined}
        aria-live="polite"
      >
        <h2 className="pb-3">
          {supplementaryTablesHeader}
          <InfoPopover
            htmlContent={true}
            content={`
            <p>These results are based on historical retirements with your specified parameters.</p>
            <p>
              Each column gives results for retirements that began within particular periods
              or that share initial market conditions.
            </p>
            <p>
              The failure rates are the percentages of historical retirements with your specified
              parameters that would have run out of money.
            </p>
            <p>Consumption amounts and rates are calculated net of any cash flows specified above.</p>`}
          />
        </h2>

        {supplementaryTables.map(table => (
          <DataTable
            key={table.id}
            caption={table.caption}
            tableData={table.data}
            onRendered={() => {
              handleTableRendered(versionRef.current)
            }}
          />
        ))}
      </section>
    </>
  )
}

export default Results
